import { isFiniteNumber } from "../../../../../../utils/number";
import { cartesianToPolar } from "../../../scheme/components/analytics/mappers/orbitMappers";
import type { IChartDataOrbit } from "../../../scheme/components/analytics/interfaces";
import type { IOrbitBandSelection } from "../../../multi-analysis/types";

export interface IOrbitBandSync {
  cursorValues: (number | null)[] | null;
  markerValues: (number | null)[] | null;
  datasetIndex: number | null;
}

const EMPTY_ORBIT_BAND_SYNC: IOrbitBandSync = {
  cursorValues: null,
  markerValues: null,
  datasetIndex: null,
};

export const placeOrbitBandPoint = (
  current: IOrbitBandSelection | null,
  panelKey: string,
  nativeX: number,
  datasetIndex: number,
): IOrbitBandSelection => {
  const continues = current?.panelKey === panelKey && current.markerNativeX != null;

  return {
    panelKey,
    markerNativeX: continues ? current.markerNativeX : nativeX,
    cursorNativeX: continues ? nativeX : null,
    datasetIndex,
  };
};

export const orbitBandSyncValues = (
  datasetKeys: readonly string[],
  selection: IOrbitBandSelection | null,
): IOrbitBandSync => {
  if (!selection || selection.markerNativeX == null) {
    return EMPTY_ORBIT_BAND_SYNC;
  }

  const markerValues = datasetKeys.map(key => (key === selection.panelKey ? selection.markerNativeX : null));
  if (!markerValues.some(value => value != null)) {
    return EMPTY_ORBIT_BAND_SYNC;
  }

  const preferred = datasetKeys.findIndex(
    (key, index) => key === selection.panelKey && index === selection.datasetIndex,
  );
  const fallback = datasetKeys.findIndex(key => key === selection.panelKey);

  return {
    markerValues,
    cursorValues:
      selection.cursorNativeX == null
        ? null
        : datasetKeys.map(key => (key === selection.panelKey ? selection.cursorNativeX : null)),
    datasetIndex: preferred >= 0 ? preferred : fallback >= 0 ? fallback : null,
  };
};

const maxAbs = (values: readonly number[]): number => values.reduce((max, value) => Math.max(max, Math.abs(value)), 0);

export const sliceOrbitToBand = (
  orbit: IChartDataOrbit,
  signal: { items: readonly { x: number }[] } | null,
  markerNativeX: number,
  cursorNativeX: number,
): IChartDataOrbit => {
  const xData = orbit.xData ?? [];
  const yData = orbit.yData ?? [];
  const length = Math.min(xData.length, yData.length);
  if (length < 2 || !isFiniteNumber(markerNativeX) || !isFiniteNumber(cursorNativeX)) {
    return orbit;
  }

  const axis = signal?.items ?? [];
  const xStart = axis.length && isFiniteNumber(axis[0].x) ? axis[0].x : 0;
  const lastX = axis.length ? axis[axis.length - 1].x : length - 1;
  const xEnd = isFiniteNumber(lastX) ? lastX : length - 1;
  const span = xEnd - xStart;
  if (!isFiniteNumber(span) || span === 0) {
    return orbit;
  }

  const ratio = (nativeX: number) => ((nativeX - xStart) / span) * (length - 1);
  const low = Math.min(ratio(markerNativeX), ratio(cursorNativeX));
  const high = Math.max(ratio(markerNativeX), ratio(cursorNativeX));
  if (low === high) {
    return orbit;
  }
  const start = Math.max(0, Math.min(length - 1, Math.floor(low)));
  const end = Math.max(0, Math.min(length - 1, Math.ceil(high)));
  if (end <= start || (start === 0 && end === length - 1)) {
    return orbit;
  }

  const slicedX = xData.slice(start, end + 1);
  const slicedY = yData.slice(start, end + 1);
  const zeroIndexes = (orbit.zeroIndexes ?? [])
    .filter(index => index >= start && index <= end)
    .map(index => index - start);
  const items = cartesianToPolar(slicedX, slicedY, zeroIndexes);
  const fraction = slicedX.length / length;

  return {
    ...orbit,
    items,
    xData: slicedX,
    yData: slicedY,
    zeroIndexes,
    param3DataMs: isFiniteNumber(orbit.param3DataMs) ? orbit.param3DataMs * fraction : orbit.param3DataMs,
    Xmax: maxAbs(slicedX),
    Ymax: maxAbs(slicedY),
    Smax: items.reduce((max, item) => Math.max(max, item.r), 0),
  };
};
